/*************************************************
 * ByzFunder
 * logrocket.ts
 * LogRocket analytics service for React + TypeScript
 *
 * Deliberately mirrors smartlook.ts's lifecycle so the two stay comparable:
 *   initLogRocket(user)  <- App.tsx / Dashboard.tsx, after login
 *   identifyUser(user)
 *   destroyLogRocket()   <- Header.tsx / McaHeader.tsx, on logout
 *
 * There is no trackNavigateStepPath() equivalent on purpose: every
 * trackNavigateStepPath() call in this app is immediately followed by a
 * react-router navigate(), and LogRocket instruments the history API, so step
 * changes already appear in the session timeline. Mirroring it would only emit
 * duplicate custom events.
 *************************************************/

const LOGROCKET_APP_ID = process.env.REACT_APP_LOGROCKET_APP_ID ?? "";

declare global {
  interface LogRocketSdk {
    init: (appId: string, options?: any) => void;
    identify: (uid: string, traits?: any) => void;
    track: (event: string, data?: any) => void;
    startNewSession: () => boolean;
  }

  interface Window {
    LogRocket?: LogRocketSdk;
    _logRocketInitialized?: boolean;
  }
}

// true once the SDK script has loaded AND init() has succeeded
let isReady = false;
// a user handed to us before the SDK was ready, identified on load instead
let pendingUser: any = null;

const identifyNow = (user: any): void => {
  if (!window.LogRocket) return;

  const visitorId =
    user?.id ??
    user?.user_id ??
    user?.userId ??
    user?.uuid ??
    user?.email ??
    "unknown";

  window.LogRocket.identify(String(visitorId), user);

  console.log("LogRocket user identified:", visitorId);
};

/**
 * Identify logged-in user.
 * Unlike Smartlook, LogRocket's snippet installs no stub that buffers calls made
 * before the script arrives, so an early call is held in pendingUser and replayed.
 */
export const identifyUser = (user?: any): void => {
  if (!user) return;

  if (isReady && window.LogRocket) {
    try {
      identifyNow(user);
    } catch (error) {
      console.warn("LogRocket identify failed:", error);
    }
    return;
  }

  pendingUser = user;
};

/**
 * Initialize LogRocket
 * Call after user login
 */
export const initLogRocket = (user?: any): void => {
  if (typeof window === "undefined") return;

  // prevent localhost tracking - same rule as smartlook.ts
  if (window.location.hostname === "localhost") return;

  try {
    // Script already injected (fully loaded, or still in flight). This covers a
    // second login in the same tab, and init being called from both App.tsx and
    // Dashboard.tsx. identifyUser() handles either case.
    if (window._logRocketInitialized) {
      identifyUser(user);
      return;
    }

    // App id missing - do not attempt to load. This is also the switch that keeps
    // LogRocket off in environments where REACT_APP_LOGROCKET_APP_ID is unset.
    if (!LOGROCKET_APP_ID) {
      console.warn(
        "LogRocket init skipped: REACT_APP_LOGROCKET_APP_ID is not set"
      );
      return;
    }

    window._logRocketInitialized = true;
    if (user) pendingUser = user;

    const script = document.createElement("script");
    script.async = true;
    script.crossOrigin = "anonymous";
    script.src = "https://cdn.logr-in.com/LogRocket.min.js";

    script.onload = () => {
      if (!window.LogRocket) {
        console.warn("LogRocket script loaded but SDK is missing");
        window._logRocketInitialized = false;
        return;
      }

      try {
        window.LogRocket.init(LOGROCKET_APP_ID);
      } catch (initError) {
        // bad app id, quota, vendor error - never let the recorder break the app
        console.warn("LogRocket init failed:", initError);
        window._logRocketInitialized = false;
        pendingUser = null;
        return;
      }

      isReady = true;
      console.log("LogRocket initialized");

      if (pendingUser) {
        const queued = pendingUser;
        pendingUser = null;
        identifyUser(queued);
      }
    };

    script.onerror = (error) => {
      // adblocker, CSP or network failure - allow a future re-init attempt
      console.warn("LogRocket script failed to load:", error);
      window._logRocketInitialized = false;
      pendingUser = null;
    };

    document.head.appendChild(script);
  } catch (error) {
    console.warn("LogRocket init failed:", error);
  }
};

/**
 * End the current LogRocket session
 * Call on logout, so the next user is not recorded into the previous session.
 * LogRocket has no anonymize() - startNewSession() is the equivalent.
 */
export const destroyLogRocket = (): void => {
  pendingUser = null;

  if (!isReady || !window.LogRocket) return;

  try {
    window.LogRocket.startNewSession();

    console.log("LogRocket session ended");
  } catch (error) {
    console.warn("LogRocket destroy failed:", error);
  }
};
